import {
  Box,
  Button,
  Breadcrumbs,
  ButtonGroup,
  MenuItem,
  Select,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { DateTime } from "luxon";
import {
  selectAllCompanyUsersById,
  selectCurrentUserAccountId,
  selectCurrentUserInfo,
  selectSampleUsersInfoById,
} from "app/containers/Global/slice";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "utils/redux/hooks";
import { useGetTeamCheckInUsersBySubjectQuery } from "app/containers/TeamsTab/teamsCheckInApi";
import {
  useGetDetectedMeetingsQuery,
  useGetUserCalendarSettingsQuery,
} from "app/containers/TeamsTab/calendarApi";
import type {
  CheckInStatus,
  SortDirection,
  TeamStatus,
} from "app/containers/TeamsTab/types";
import { MediumCustomIcon, SmallCustomIcon } from "../../layer00";
import { StyledTextField } from "../../layer01";
import { TeamDateRangeFilter } from "../../layer06";
import { TeamTable } from "../../layer25";
import { getViewKey, useMyTeamsNav } from "./useMyTeamsNav";
import { OneOnOnePrepModal } from "../..";
import { useParams } from "../../../common";

export const TeamActivityDetailSection = () => {
  const currentUserAccountId = useAppSelector(selectCurrentUserAccountId);

  if (!currentUserAccountId) return null;

  return <MyTeamsView callerId={currentUserAccountId} />;
};

const MyTeamsView = ({ callerId }: { callerId: number }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const nav = useMyTeamsNav(callerId);

  const [status, setStatus] = useState<TeamStatus>("all");
  const [checkInStatus, setCheckInStatus] = useState<CheckInStatus>("all");
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // A 1:1 prep deep link lands here with the person to open the prep drawer for
  const { oneOnOnePrepUserAccountId } = useParams<{
    oneOnOnePrepUserAccountId?: string;
  }>();
  const [selectedUser, setSelectedUser] = useState<{
    userAccountId: number | null;
    date?: string | null;
    mostRecentCompletedCheckIn?: boolean;
  }>(() => ({
    userAccountId: oneOnOnePrepUserAccountId
      ? Number(oneOnOnePrepUserAccountId)
      : null,
    mostRecentCompletedCheckIn: !!oneOnOnePrepUserAccountId,
  }));

  const viewKey = getViewKey(nav.current);
  useEffect(() => {
    setStatus("all");
    setCheckInStatus("all");
    setStartDate(undefined);
    setEndDate(undefined);
    setSortBy("");
    setSortDirection("asc");
    setSearchInput("");
    setDebouncedSearch("");
  }, [viewKey]);

  const handleSort = (value: string, direction: SortDirection) => {
    setSortBy(value);
    setSortDirection(direction);
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const apiStatus = status !== "all" ? status : undefined;
  const apiCheckInStatus = checkInStatus !== "all" ? checkInStatus : undefined;
  const apiSortBy = sortBy || undefined;
  const apiSortDirection = sortBy ? sortDirection : undefined;
  const apiSearch = debouncedSearch || undefined;

  const hasActiveFilters =
    status !== "all" ||
    checkInStatus !== "all" ||
    debouncedSearch !== "" ||
    !!startDate;
  const isAtRoot = nav.breadcrumbs.length === 1;
  const currentBreadcrumbLabel = nav.breadcrumbs.at(-1)?.label ?? "";

  const usersBySubjectQuery = useGetTeamCheckInUsersBySubjectQuery(
    nav.current.view === "users-by-subject"
      ? {
          subjectUserAccountId: nav.current.subjectUserId,
          startDate,
          endDate,
          checkInStatus: apiCheckInStatus,
          status: apiStatus,
          sortBy: apiSortBy as any,
          sortDirection: apiSortDirection,
          searchString: apiSearch,
        }
      : skipToken
  );

  const sampleUsersById = useAppSelector(selectSampleUsersInfoById);
  const companyUsersById = useAppSelector(selectAllCompanyUsersById);

  const usersById = useMemo(
    () => ({ ...sampleUsersById, ...companyUsersById }),
    [sampleUsersById, companyUsersById]
  );

  const currentUserInfo = useAppSelector(selectCurrentUserInfo);
  const { data: calendarSettings } = useGetUserCalendarSettingsQuery();
  const showNextOneOnOne = !!calendarSettings?.isCalendarEnabled;
  const { data: meetingsData } = useGetDetectedMeetingsQuery(
    showNextOneOnOne ? undefined : skipToken
  );

  const nextOneOnOneByUserId = useMemo(() => {
    const map: Record<number, DateTime> = {};
    const currentUserEmail = currentUserInfo?.emailAddress;
    const userIdByEmail = Object.fromEntries(
      Object.entries(usersById).map(([id, user]) => [
        user.emailAddress,
        Number(id),
      ])
    );
    for (const meeting of meetingsData?.detected_1on1s ?? []) {
      if (meeting.participants.length !== 2) continue;
      const startTime = DateTime.fromISO(meeting.startTime);
      if (startTime <= DateTime.now()) continue;
      const otherParticipant = meeting.participants.find(
        (participant) => participant.email !== currentUserEmail
      );
      if (!otherParticipant) continue;
      const userAccountId = userIdByEmail[otherParticipant.email];
      if (userAccountId === undefined) continue;
      if (!map[userAccountId] || startTime < map[userAccountId]) {
        map[userAccountId] = startTime;
      }
    }
    return map;
  }, [meetingsData, usersById, currentUserInfo?.emailAddress]);

  const handleUserClick = (userAccountId: number, displayName: string) => {
    nav.navigateToPerson(userAccountId, displayName);
  };

  const handleViewSummary = (userAccountId: number, date?: string | null) => {
    setSelectedUser({ userAccountId, date });
  };

  const handleBreadcrumbClick = (index: number) => {
    nav.navigateToBreadcrumb(index);
  };

  const getEmptyStateNode = () => {
    if (hasActiveFilters) {
      return (
        <TeamTableEmptyState
          title="No results found"
          body="Try adjusting your filters or search term."
        />
      );
    }

    if (isAtRoot) {
      return (
        <TeamTableEmptyState
          title="No team members"
          body="You aren't currently a team leader or have any direct reports."
        />
      );
    }

    return (
      <TeamTableEmptyState
        title={`${currentBreadcrumbLabel} doesn't lead any members`}
        body={`${currentBreadcrumbLabel} has no direct reports.`}
      />
    );
  };

  return (
    <Box
      sx={{
        marginTop: { xs: ".5rem", sm: "1.5rem" },
        paddingX: { xs: 0, sm: ".5rem" },
      }}
    >
      <Stack rowGap="1.5rem">
        {/* Search row */}
        <Stack direction="row" columnGap="1rem" alignItems="flex-end">
          <Box flex={{ xs: "1 1 0", sm: "3 1 0" }} minWidth={0}>
            <StyledTextField
              placeholder="Search"
              value={searchInput}
              sx={{
                paddingTop: 0,
                paddingBottom: 0,
                height: "2.5rem",
                marginTop: 0,
              }}
              startAdornment={
                <MediumCustomIcon
                  icon="SEARCH"
                  stroke="var(--team-app-search-icon)"
                />
              }
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </Box>
          <Box flex="1 1 0" minWidth={0}>
            <TeamDateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }}
            />
          </Box>
        </Stack>

        {/* Status filter + Check-in status filter + Date range filter */}
        <Stack
          direction="row"
          columnGap={{ xs: "1rem", sm: "1.5rem" }}
          alignItems="flex-end"
          flexWrap="wrap"
          rowGap="1rem"
        >
          <StatusFilter
            value={status}
            onChange={setStatus}
            isMobile={isMobile}
          />
          <CheckInStatusFilter
            value={checkInStatus}
            onChange={setCheckInStatus}
            isMobile={isMobile}
          />
        </Stack>
        <Box
          className="bg-white border border-teamApp-filter-active-bg"
          paddingTop="1rem"
          paddingBottom="2rem"
          sx={{
            paddingX: { xs: 0, sm: "1rem" },
            borderRadius: "1rem",
            overflow: "hidden",
          }}
        >
          <Breadcrumbs
            separator="|"
            sx={{
              marginBottom: "1rem",
              paddingLeft: ".875rem",
              "& .MuiBreadcrumbs-separator": {
                color: "var(--sapphire-30)",
                marginLeft: ".625rem",
                marginRight: ".625rem",
              },
            }}
          >
            {nav.breadcrumbs.map((crumb, i) => (
              <Button
                key={`${crumb.label}-${i}`}
                variant="text"
                onClick={() => handleBreadcrumbClick(i)}
                sx={{
                  textTransform: "none",
                  fontSize: ".9375rem",
                  fontWeight: 500,
                  minWidth: 0,
                  padding: 0,
                  gap: ".375rem",
                  color:
                    i === nav.breadcrumbs.length - 1
                      ? "var(--team-app-interactive-active)"
                      : "var(--team-app-text-secondary)",
                  "&:hover": { backgroundColor: "transparent" },
                }}
              >
                {crumb.showPersonnelIcon && (
                  <SmallCustomIcon
                    icon="PERSONNEL"
                    stroke="var(--team-app-text-secondary)"
                  />
                )}
                {crumb.label}
              </Button>
            ))}
          </Breadcrumbs>

          <TeamTable
            items={usersBySubjectQuery.data ?? []}
            usersById={usersById}
            isLoading={usersBySubjectQuery.isFetching}
            onUserClick={handleUserClick}
            onViewSummary={handleViewSummary}
            hasDateRange={!!startDate}
            sortBy={apiSortBy}
            sortDirection={apiSortDirection}
            onSort={handleSort}
            emptyStateNode={getEmptyStateNode()}
            showNextOneOnOne={showNextOneOnOne}
            nextOneOnOneByUserId={nextOneOnOneByUserId}
          />
        </Box>
      </Stack>

      {!!selectedUser.userAccountId && (
        <OneOnOnePrepModal
          id={`one-on-one-prep-modal-${selectedUser.userAccountId}`}
          isVisible
          userAccountId={selectedUser.userAccountId}
          handleClose={() => setSelectedUser({ userAccountId: null })}
          date={selectedUser.date}
          mostRecentCompletedCheckIn={selectedUser.mostRecentCompletedCheckIn}
        />
      )}
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Empty state                                                         */
/* ------------------------------------------------------------------ */

const TeamTableEmptyState = ({
  title,
  body,
}: {
  title: string;
  body: string;
}) => (
  <Stack alignItems="center" rowGap=".375rem">
    <Typography
      className="text-teamApp-text-secondary font-semibold"
      sx={{ fontSize: ".9375rem" }}
    >
      {title}
    </Typography>
    <Typography className="text-teamApp-text-placeholder-dark text-sm">
      {body}
    </Typography>
  </Stack>
);

/* ------------------------------------------------------------------ */
/* Filter sub-components                                               */
/* ------------------------------------------------------------------ */

const toggleGroupSx = {
  height: "3rem",
  borderRadius: ".75rem",
  border: "1px solid var(--team-app-filter-active-border)",
  background: "var(--team-app-gradient-stripe-filter)",
  padding: ".25rem",
  columnGap: ".5rem",
  "& .MuiButtonGroup-grouped": {
    borderRadius: ".5rem",
    border: "1px solid var(--team-app-filter-active-bg)",
    padding: ".5rem",
    whiteSpace: "nowrap",
  },
};

const selectedBtnSx = { "&:hover": { backgroundColor: "white" } };
const textBtnSx = { "&:hover": { backgroundColor: "transparent" } };

const statusOptions: { value: TeamStatus; label: string }[] = [
  { value: "all", label: "All" },
  { value: "need-support", label: "Need support" },
  { value: "no-issues", label: "No issues" },
];

const StatusFilter = ({
  value,
  onChange,
  isMobile,
}: {
  value: TeamStatus;
  onChange: (v: TeamStatus) => void;
  isMobile: boolean;
}) => {
  if (isMobile) {
    return (
      <Stack direction="column" flex="1 1 0" minWidth={0}>
        <Typography variant="formLabel">Filter on status</Typography>
        <Select
          value={value}
          variant="standard"
          disableUnderline
          onChange={(e) => onChange(e.target.value as TeamStatus)}
          sx={{ marginTop: ".25rem" }}
        >
          {statusOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    );
  }

  return (
    <Stack direction="column">
      <Typography variant="formLabel">Filter on status</Typography>
      <ButtonGroup sx={toggleGroupSx}>
        {statusOptions.map((o) => (
          <Button
            key={o.value}
            variant={value === o.value ? "outlined" : "text"}
            color={value === o.value ? "primary" : "secondary"}
            sx={value === o.value ? selectedBtnSx : textBtnSx}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </Button>
        ))}
      </ButtonGroup>
    </Stack>
  );
};

const checkInStatusOptions: { value: CheckInStatus; label: string }[] = [
  { value: "all", label: "All" },
  { value: "checked-in", label: "Checked in" },
  { value: "not-checked-in", label: "Not checked in" },
];

const CheckInStatusFilter = ({
  value,
  onChange,
  isMobile,
}: {
  value: CheckInStatus;
  onChange: (v: CheckInStatus) => void;
  isMobile: boolean;
}) => {
  if (isMobile) {
    return (
      <Stack direction="column" flex="1 1 0" minWidth={0}>
        <Typography variant="formLabel">Filter on check-in</Typography>
        <Select
          value={value}
          variant="standard"
          disableUnderline
          onChange={(e) => onChange(e.target.value as CheckInStatus)}
          sx={{ marginTop: ".25rem" }}
        >
          {checkInStatusOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    );
  }

  return (
    <Stack direction="column">
      <Typography variant="formLabel">Filter on check-in</Typography>
      <ButtonGroup sx={toggleGroupSx}>
        {checkInStatusOptions.map((o) => (
          <Button
            key={o.value}
            variant={value === o.value ? "outlined" : "text"}
            color={value === o.value ? "primary" : "secondary"}
            sx={value === o.value ? selectedBtnSx : textBtnSx}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </Button>
        ))}
      </ButtonGroup>
    </Stack>
  );
};
